import { t } from 'i18next';
import PropTypes from 'prop-types';

import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AddressInput from 'components/field/AddressInput';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import * as F from 'components/form/FormLayout.styled';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useLocale from 'hooks/useLocale';
import useUpdateEffect from 'hooks/useUpdateEffect';

import {
	getCityByCountry,
	getCityByState,
	getCountryList,
	getDistrictByCity,
	getStateByCountry
} from 'stores/globalAddressSlice';

import AppLogger from 'utils/AppLogger';

import AutoString from './AutoString';
import EnumSelect from './EnumSelect';

/**
 * @typedef {object} AddressGroupProps
 * @property {string} [formFieldName] - Form field to bound this component's children.
 * @property {object} [fieldsColspan] - Field config to show/hide, and scale column extension of the fields.
 * @property {object} [requiredFields] - Required fields config.
 * @property {object} [formMethods] - Form methods from the parent component.
 * @property {boolean} [disabled] - If true, the form item is disabled.
 * @property {boolean} [isLoading] - If true, the form item is loading.
 * @property {number} [colSpan] - Column span of the form item.
 * @property {object} [labels]  - Labels for the fields. country | state | city | district (optional)
 * @property {boolean} [selectFromMap] - If true, the map button is enabled.
 * @property {boolean} [lineSeperator] - If true, the line seperator is shown.
 * @property {object} [restrictions] - Restrictions for the fields.
 * @param {*} props
 * @return {*}
 */
const AddressGroup = (props) => {
	// Check if formFieldName is defined
	const { formFieldName, disabled } = props;
	if (!formFieldName) AppLogger.error('Please define the "formFieldName" for AddressGroup');

	// form Methods
	const formMethods = useFormContext();
	if (!formMethods) throw new Error('AddressGroup: missing form context!');
	const { setValue, getValues } = formMethods;
	const countryNameInForm = getValues(formFieldName)?.countryName ?? null;

	// Custom Hooks
	const { showError } = useAlert();
	const dispatch = useDispatch();

	const { LanguageCode } = useLocale();

	// Service Mapping
	const sliceMapping = useMemo(
		() => ({
			country: {
				key: 'country',
				slice: getCountryList,
				mapKeys: (data) => ({
					shortCode: data?.shortCode
				})
			},
			state: {
				key: 'state',
				slice: getStateByCountry,
				mapKeys: (data) => ({
					name: data?.name
				})
			},
			cityByCountry: {
				key: 'city',
				slice: getCityByCountry,
				mapKeys: (data) => ({
					name: data?.name
				})
			},
			cityByState: {
				key: 'city',
				slice: getCityByState,
				mapKeys: (data) => ({
					name: data?.name
				})
			},
			district: {
				key: 'district',
				slice: getDistrictByCity,
				mapKeys: (data) => ({
					name: data?.name
				})
			}
		}),
		[LanguageCode]
	);

	// States
	const [searchTerm, setSearchTerm] = useState(null);
	const [fetchedData, setFetchedData] = useState({
		country: [],
		state: [],
		city: [],
		district: []
	});

	// Helpers
	const fetchData = useCallback(
		async (sliceMap, payload = {}) =>
			await dispatch(sliceMap.slice(payload))
				.unwrap()
				.then((response) => {
					let mappedData = [];

					if (sliceMap.key === 'country') {
						mappedData = response?.map((item) => {
							const enumItem = sliceMap.mapKeys(item);
							return Object.values(enumItem).join(' , ');
						});
					} else {
						mappedData = response?.data?.map((item) => {
							const enumItem = sliceMap.mapKeys(item);
							return Object.values(enumItem).join(' , ');
						});
					}

					setFetchedData((prevState) => ({
						...prevState,
						[sliceMap.key]: mappedData
					}));
					return mappedData ?? [];
				})
				.catch((error) => {
					showError();
					AppLogger.error(
						`AddressGroup : error while fetching ${JSON.stringify(payload)} with error ${JSON.stringify(
							error
						)}.`
					);
				}),

		[]
	);

	const fetchCountryBoundings = useCallback(
		async (country) => {
			await fetchData(sliceMapping.cityByCountry, country);
			await fetchData(sliceMapping.state, country);
		},
		[sliceMapping]
	);

	const resetFetchedData = useCallback((fields) => {
		setFetchedData((prevState) => ({
			...prevState,
			...fields.reduce((acc, field) => ({ ...acc, [field]: [] }), {})
		}));
	}, []);

	const resetFields = useCallback((fields) => {
		fields?.forEach((field) => {
			setValue(`${formFieldName}.${field}`, null);
		});
	}, []);

	const handleChange = useCallback(async (payload) => {
		const { name, value } = payload;
		const valueToSet = !value.trim() ? null : value.split(' , ')[0];
		switch (name) {
			case 'countryName':
				if (valueToSet) {
					fetchCountryBoundings(valueToSet);
				}
				resetFetchedData(['state', 'city', 'district']);
				resetFields(['stateName', 'cityName', 'districtName']);
				return;
			case 'stateName':
				if (valueToSet) {
					fetchData(sliceMapping.cityByState, valueToSet);
					resetFetchedData(['city', 'district']);
					resetFields(['cityName', 'districtName']);
				} else {
					resetFetchedData(['city', 'district']);
					resetFields(['cityName', 'districtName']);
					const countryName = getValues(`${formFieldName}.countryName`);
					if (countryName) {
						await fetchData(sliceMapping.cityByCountry, countryName);
					}
				}
				return;
			case 'cityName':
				if (valueToSet) {
					fetchData(sliceMapping.district, valueToSet);
				} else {
					resetFetchedData(['district']);
					resetFields(['districtName']);
				}
				return;
			// case 'district':
			// 	break;
		}
	}, []);

	const setSelectedValue = useCallback((name, value) => {
		setValue(name, value ?? null, {
			shouldValidate: true,
			shouldDirty: true
		});
	}, []);

	const addressInputChanged = useCallback(async (data) => {
		const { country = null, state = null, city = null } = data;

		fetchData(sliceMapping.country, {}).then(() => {
			if (country?.name) {
				fetchCountryBoundings(country.name);
			}
			state?.name && fetchData(sliceMapping.cityByState, state.name);
			city?.name && fetchData(sliceMapping.district, city.name);
		});
	}, []);

	// Effects
	useEffect(() => {
		if (disabled || !formFieldName) return;

		resetFields(['countryID', 'cityID']);

		fetchData(sliceMapping.country, {}).then(() => {
			const { countryName = null, stateName = null, cityName = null } = getValues(formFieldName) || {};
			if (countryName) {
				fetchCountryBoundings(countryName);
			}
			stateName && fetchData(sliceMapping.cityByState, stateName);
			cityName && fetchData(sliceMapping.district, cityName);
		});
	}, [countryNameInForm]);

	useUpdateEffect(() => {
		if (!searchTerm) return;

		setSelectedValue(`${formFieldName}.${searchTerm.name}`, searchTerm?.value ?? null);

		const id = setTimeout(() => {
			handleChange(searchTerm);
		}, 500);

		return () => clearTimeout(id);
	}, [searchTerm]);

	// Render
	return (
		<View
			{...props}
			data={fetchedData}
			handleSearch={setSearchTerm}
			resetFields={resetFields}
			addressInputChanged={addressInputChanged}
		/>
	);
};

const View = ({
	formFieldName = null,
	fieldsColspan = {
		address: {
			sm: 12
		},
		country: {
			md: 6
		},
		state: {
			md: 6
		},
		city: {
			md: 6
		},
		district: {
			md: 6
		},
		zipCode: {
			md: 6
		},
		lat: {
			md: 3
		},
		lng: {
			md: 3
		}
	},
	requiredFields = {
		address: true,
		country: true,
		state: false,
		city: true,
		district: false,
		zipCode: false,
		lat: false,
		lng: false
	},
	disabledFields = {
		address: false,
		country: false,
		state: false,
		city: false,
		district: false,
		zipCode: false,
		lat: false,
		lng: false
	},
	customTextFields = {
		country: false,
		state: true,
		city: true,
		district: true
	},
	disabled,
	isLoading = false,
	labels = {
		address: 'address',
		country: 'country',
		state: 'stateOrRegion',
		city: 'city',
		district: 'district'
	},
	selectFromMap = true,
	lineSeperator = false,
	restrictions = null,
	data,
	handleSearch,
	resetFields,
	addressInputChanged
}) => {
	// form Methods
	const formMethods = useFormContext();

	if (!formMethods) throw new Error('AddressGroup: missing form context!');

	const { control } = formMethods;

	// Fields Colspan
	const {
		address: addressColspan,
		country: countryColspan,
		state: stateColspan,
		city: cityColspan,
		district: districtColspan,
		zipCode: zipCodeColspan,
		lat: latColspan,
		lng: lngColspan
	} = fieldsColspan;

	// required Fields
	const {
		address: addressRequired = true,
		country: countryRequired = true,
		state: stateRequired = false,
		city: cityRequired = true,
		district: districtRequired = false,
		zipCode: zipCodeRequired = false,
		lat: latRequired = false,
		lng: lngRequired = false
	} = requiredFields;

	// custom text Fields
	const {
		country: countryCustomText = false,
		state: stateCustomText = true,
		city: cityCustomText = true,
		district: districtCustomText = true
	} = customTextFields;

	// labels
	const {
		address = 'address',
		country = 'country',
		state = 'stateOrRegion',
		city = 'city',
		district = 'district'
	} = labels;

	// watched Fields
	const countryName = useWatch({
		control,
		name: `${formFieldName}.countryName`
	});

	const stateName = useWatch({
		control,
		name: `${formFieldName}.stateName`
	});

	const cityName = useWatch({
		control,
		name: `${formFieldName}.cityName`
	});

	const districtName = useWatch({
		control,
		name: `${formFieldName}.districtName`
	});

	return (
		<>
			{/* Address - Text */}
			{!!addressColspan && (
				<FormItem colSpan={addressColspan} label={address} isLoading={isLoading} required={addressRequired}>
					<AddressInput
						showButton={selectFromMap}
						disabled={restrictions?.address?.isNotDifferent || disabledFields.address || disabled}
						name={formFieldName}
						restrictions={restrictions}
						onAddressChange={(data) => addressInputChanged(data)}
					/>
				</FormItem>
			)}
			{/* Address - text*/}
			{/* Country */}
			{!!countryColspan && (
				<>
					{lineSeperator && <F.Line className="line" />}
					<FormItem colSpan={countryColspan} label={country} isLoading={isLoading} required={countryRequired}>
						<>
							{createElement(countryCustomText ? AutoString : EnumSelect, {
								name: `${formFieldName}.countryName`,
								placeholder: 'country',
								data: data?.country ?? [],
								renderSelectionValue: (value) => (
									<F.FlagOptionContainer className="selected">
										<span
											className={`oto-country-flag flag-${value.split('.')[1]?.toLowerCase()}`}
										/>
										<Typography>{value ?? null}</Typography>
									</F.FlagOptionContainer>
								),
								renderOption: (value) => (
									<F.FlagOptionContainer>
										<span className={`oto-country-flag flag-${value?.toLowerCase()}`} />
										<Typography>{`country.${value?.toUpperCase()}`}</Typography>
									</F.FlagOptionContainer>
								),
								value: t(countryName) ?? restrictions?.country?.code?.toUpperCase() ?? null,
								setValue: (value) => {
									if (restrictions?.country?.isNotDifferent) return;
									handleSearch({ name: 'countryName', value });
									resetFields(['stateName', 'cityName', 'districtName']);
								},
								disabled: restrictions?.country?.isNotDifferent || disabledFields.country || disabled,
								...(!countryCustomText && { labelPrefix: 'country.', valueRemovable: false })
							})}
						</>
					</FormItem>
				</>
			)}
			{/* Country */}
			{/* state */}
			{!!stateColspan && (
				<>
					{lineSeperator && <F.Line className="line" />}
					<FormItem colSpan={stateColspan} label={state} isLoading={isLoading} required={stateRequired}>
						<>
							{createElement(stateCustomText ? AutoString : EnumSelect, {
								name: `${formFieldName}.stateName`,
								placeholder: 'state',
								data: data?.state ?? [],
								value: t(stateName) ?? null,
								setValue: (value) => {
									handleSearch({ name: 'stateName', value });
									resetFields(['cityName', 'districtName']);
								},
								disabled:
									restrictions?.state?.isNotDifferent ||
									!countryName ||
									disabledFields.state ||
									disabled
							})}
						</>
					</FormItem>
				</>
			)}
			{/* state */}
			{/* City */}
			{!!cityColspan && (
				<>
					{lineSeperator && <F.Line className="line" />}
					<FormItem colSpan={cityColspan} label={city} isLoading={isLoading} required={cityRequired}>
						<>
							{createElement(cityCustomText ? AutoString : EnumSelect, {
								name: `${formFieldName}.cityName`,
								placeholder: 'city',
								data: data?.city ?? [],
								value: t(cityName) ?? null,
								setValue: (value) => {
									handleSearch({ name: 'cityName', value });
									resetFields(['districtName']);
								},
								disabled:
									restrictions?.city?.isNotDifferent ||
									!countryName ||
									disabledFields.city ||
									disabled
							})}
						</>
					</FormItem>
				</>
			)}
			{/* City */}
			{/* district */}
			{!!districtColspan && (
				<>
					{lineSeperator && <F.Line className="line" />}
					<FormItem
						colSpan={districtColspan}
						label={district}
						isLoading={isLoading}
						required={districtRequired}>
						<>
							{createElement(districtCustomText ? AutoString : EnumSelect, {
								name: `${formFieldName}.districtName`,
								placeholder: 'district',
								data: data?.district ?? [],
								value: t(districtName) ?? null,
								setValue: (value) => {
									handleSearch({ name: 'districtName', value });
								},
								disabled:
									restrictions?.district?.isNotDifferent ||
									!cityName ||
									disabledFields.district ||
									disabled
							})}
						</>
					</FormItem>
				</>
			)}
			{/* district */}
			{/* latitude */}
			{!!latColspan && (
				<>
					{lineSeperator && <F.Line className="line" />}
					<FormItem colSpan={latColspan} label="latitude" isLoading={isLoading} required={latRequired}>
						<StringInput disabled={disabledFields.lat || disabled} name={`${formFieldName}.lat`} />
					</FormItem>
				</>
			)}
			{/* latitude */}
			{/* longitude */}
			{!!lngColspan && (
				<>
					{lineSeperator && <F.Line className="line" />}
					<FormItem colSpan={lngColspan} label="longitude" isLoading={isLoading} required={lngRequired}>
						<StringInput disabled={disabledFields.lng || disabled} name={`${formFieldName}.lng`} />
					</FormItem>
				</>
			)}
			{/* longitude */}
			{/* ZipCode */}
			{!!zipCodeColspan && (
				<>
					{lineSeperator && <F.Line className="line" />}
					<FormItem colSpan={zipCodeColspan} label="zipCode" isLoading={isLoading} required={zipCodeRequired}>
						<StringInput disabled={disabledFields.zipCode || disabled} name={`${formFieldName}.zipCode`} />
					</FormItem>
				</>
			)}
			{/* ZipCode */}
		</>
	);
};

export default AddressGroup;

// proptypes documentation
AddressGroup.propTypes = {
	/** Form field to bound this component's children. */
	formFieldName: PropTypes.string,
	/** Field config to show/hide, and scale column extension of the fields. */
	fieldsColspan: PropTypes.object,
	/** Form methods from the parent component. */
	formMethods: PropTypes.object,
	/** If true, the form item is disabled. */
	disabled: PropTypes.bool,
	/** If true, the form item is loading. */
	isLoading: PropTypes.bool,
	/** Column span of the form item. */
	colSpan: PropTypes.number,
	/** Labels for the fields. country | state | city | district (optional) */
	labels: PropTypes.object,
	/** If true, the map button is enabled. */
	selectFromMap: PropTypes.bool,
	/** If true, the line seperator is shown. */
	lineSeperator: PropTypes.bool,
	/** Restrictions for the fields. */
	restrictions: PropTypes.object
};
